import React from "react";
import { useEmailHealthReport } from "./email-health-context";
import PrintContentButton from "@/components/print-content-button";
import PublishReportButton from "../publish-button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import StyledTooltip from "@/components/styled-tooltip";
import { AlertCircle, CheckCircle, Info, Shield, XCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { Progress } from "@/components/ui/progress";

export const EmailHealthHeader = () => {
  const { recordsData, state, setState, toggleCollapseAll } =
    useEmailHealthReport();
  return (
    <div className="flex flex-col md:flex-row items-center justify-between mb-4 gap-2">
      <div className="text-center md:text-left">
        <h1 className="page-heading">Domain Health Test Report</h1>
        <p className="text-sm text-muted-foreground">{recordsData?.domain}</p>
      </div>
      {recordsData && (
        <div className="flex flex-col items-center md:items-end gap-2">
          <div className="flex items-center gap-2">
            {/* print content button */}
            <PrintContentButton
              id="email-health-print"
              type="email-health"
              preRunCallback={() => {
                toggleCollapseAll("expand");
                setState((prev) => ({
                  ...prev,
                  isPrinting: true,
                }));
                window.cioanalytics?.track("email_health_test_printed", {
                  domain: recordsData?.domain,
                });
              }}
              postRunCallback={() => {
                toggleCollapseAll("collapse");
                setState((prev) => ({
                  ...prev,
                  isPrinting: false,
                }));
              }}
              fileName={recordsData?.domain}
              // triggerClassName="[&>span]:hidden sm:[&>span]:block"
            />

            {/* publish report button */}
            {!state.isPrinting && (
              <PublishReportButton
                domain={recordsData?.domain}
                companyId={recordsData.companyId}
                reportId={recordsData._id}
                isPublic={recordsData.isPublic}
                type="details-page"
              />
            )}
          </div>
          {/* published at */}
          {recordsData.isPublic && (
            <div className="flex items-center md:text-right gap-2 text-sm text-muted-foreground">
              <span>Published at</span>
              <span>
                {new Date(recordsData.publishedAt).toLocaleString("en-GB", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: true,
                })}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const EmailHealthSummaryCards = () => {
  const { recordsData } = useEmailHealthReport();

  return (
    <div className="grid gap-4 md:grid-cols-3">
      <Card
        className={cn("bg-gradient-to-br", {
          "from-red-50 to-red-100/50 dark:from-red-900/20 dark:to-red-900/20":
            (recordsData?.healthcheck?.score || 0) < 50,
          "from-yellow-50 to-yellow-100/50 dark:from-yellow-900/20 dark:to-yellow-900/20":
            (recordsData?.healthcheck?.score || 0) >= 50 &&
            (recordsData?.healthcheck?.score || 0) < 75,
          "from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-900/20":
            (recordsData?.healthcheck?.score || 0) >= 75,
        })}
      >
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            Overall Score
            <StyledTooltip description="The overall score evaluates your domain health by combining results from all performed checks, DNS records verification, and blacklist status checks.">
              <Info className="w-3 h-3 text-muted-foreground" />
            </StyledTooltip>
          </CardTitle>
        </CardHeader>
        <CardContent className="">
          <div className="flex flex-col items-center">
            <Tooltip>
              <TooltipTrigger>
                <div className="relative w-32 h-32">
                  {/* Background Circle */}
                  <svg
                    className="absolute w-full h-full -rotate-90"
                    viewBox="0 0 100 100"
                  >
                    <circle
                      className="stroke-gray-200"
                      cx="50"
                      cy="50"
                      r="46"
                      fill="none"
                      strokeWidth="8"
                    />
                  </svg>
                  {/* Progress Circle */}
                  <svg
                    className="absolute w-full h-full -rotate-90"
                    viewBox="0 0 100 100"
                  >
                    <circle
                      className={cn(
                        "transition-all duration-1000 ease-in-out",
                        {
                          "stroke-red-500":
                            (recordsData?.healthcheck?.score || 0) < 50,
                          "stroke-yellow-500":
                            (recordsData?.healthcheck?.score || 0) >= 50 &&
                            (recordsData?.healthcheck?.score || 0) < 75,
                          "stroke-blue-500":
                            (recordsData?.healthcheck?.score || 0) >= 75,
                        }
                      )}
                      cx="50"
                      cy="50"
                      r="46"
                      fill="none"
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={`${
                        ((recordsData?.healthcheck?.score || 0) / 100) * 289
                      } 289`}
                    />
                  </svg>
                  {/* Score Display */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-4xl font-bold">
                      {recordsData?.healthcheck?.score || 0}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      of 100
                    </span>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <div className="text-sm">
                  <p>Score: {recordsData?.healthcheck?.score || 0}/100</p>
                  <p>
                    Percentage:{" "}
                    {(
                      ((recordsData?.healthcheck?.score || 0) / 100) *
                      100
                    ).toFixed(1)}
                    %
                  </p>
                </div>
              </TooltipContent>
            </Tooltip>

            <div className="w-full pt-4">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-muted-foreground">Total Checks</span>
                  <div className="flex items-center gap-2">
                    <span className="text-foreground font-semibold">
                      {recordsData?.healthcheck?.totalChecks || 0}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-muted-foreground">Passed Checks</span>
                  <div className="flex items-center gap-2">
                    <span className="text-foreground font-semibold">
                      {recordsData?.healthcheck?.passedChecks || 0}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-muted-foreground">Last Check</span>
                  <div className="flex items-center gap-2">
                    <span className="text-foreground font-semibold">
                      {recordsData?.lastChecked
                        ? new Date(recordsData.lastChecked).toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )
                        : "Never"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Status Card */}
      <Card
        className={cn("bg-gradient-to-br", {
          "bg-red-50 dark:bg-red-900/20":
            (recordsData?.healthcheck?.passedChecks || 0) <
            (recordsData?.healthcheck?.totalChecks || 0) * 0.5,
          "bg-yellow-50 dark:bg-yellow-900/20":
            (recordsData?.healthcheck?.passedChecks || 0) >=
            (recordsData?.healthcheck?.totalChecks || 0) * 0.5,
          "bg-green-50 dark:bg-green-900/20":
            (recordsData?.healthcheck?.passedChecks || 0) >=
            (recordsData?.healthcheck?.totalChecks || 0) * 0.75,
        })}
      >
        <CardContent className="p-6">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-muted-foreground">
              Security Score
            </h3>
            <StyledTooltip description="The security score is a percentage of the checks that passed.">
              <Info className="w-3 h-3 text-muted-foreground" />
            </StyledTooltip>
          </div>
          <div className="mt-2">
            <div
              className={cn("flex items-center justify-between gap-2", {
                "text-green-500":
                  (recordsData?.healthcheck?.passedChecks || 0) >=
                  (recordsData?.healthcheck?.totalChecks || 1) * 0.75,
                "text-yellow-500":
                  (recordsData?.healthcheck?.passedChecks || 0) >=
                  (recordsData?.healthcheck?.totalChecks || 1) * 0.5,
                "text-red-500":
                  (recordsData?.healthcheck?.passedChecks || 0) <
                  (recordsData?.healthcheck?.totalChecks || 1) * 0.5,
              })}
            >
              <span className={cn("text-2xl font-semibold mb-2")}>
                {(
                  ((recordsData?.healthcheck?.passedChecks || 0) /
                    (recordsData?.healthcheck?.totalChecks || 1)) *
                  100
                ).toFixed(0)}
                %
              </span>

              <Shield className={cn("h-6 w-6")} />
            </div>
            <Progress
              className="h-5 bg-neutral-100"
              indicatorClassName={cn({
                "bg-green-500":
                  (recordsData?.healthcheck?.passedChecks || 0) >=
                  (recordsData?.healthcheck?.totalChecks || 1) * 0.75,
                "bg-yellow-500":
                  (recordsData?.healthcheck?.passedChecks || 0) >=
                  (recordsData?.healthcheck?.totalChecks || 1) * 0.5,
                "bg-red-500":
                  (recordsData?.healthcheck?.passedChecks || 0) <
                  (recordsData?.healthcheck?.totalChecks || 1) * 0.5,
              })}
              value={
                ((recordsData?.healthcheck?.passedChecks || 0) /
                  (recordsData?.healthcheck?.totalChecks || 1)) *
                100
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Status Summary Card */}
      <Card className="bg-orange-50 dark:bg-orange-900/20">
        <CardContent className="p-6">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-muted-foreground">
              Records Status
            </h3>
            <StyledTooltip description="The records status is a summary of the status of the DNS records of your domain.">
              <Info className="w-3 h-3 text-muted-foreground" />
            </StyledTooltip>
          </div>
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between gap-2 bg-gradient-to-r from-transparent to-red-200/30 p-2 pr-3 rounded-md">
              {/* <div className="h-2 w-2 rounded-full bg-red-500" /> */}
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-500" />
                <span className="text-sm text-foreground">Failed</span>
              </div>
              <span className="text-xl font-semibold text-red-500">
                {recordsData?.healthcheck?.checkResults?.filter(
                  (r) => r.status === "fail"
                ).length || 0}
              </span>
            </div>
            <div>
              <div className="flex items-center justify-between gap-2 bg-gradient-to-r from-transparent to-yellow-200/30 p-2 pr-3 rounded-md">
                {/* <div className="h-2 w-2 rounded-full bg-yellow-400" /> */}
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm text-foreground">Warnings</span>
                </div>
                <span className="text-xl font-semibold text-yellow-500">
                  {recordsData?.healthcheck?.warnings || 0}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 bg-gradient-to-r from-transparent to-green-200/30 p-2 pr-3 rounded-md">
              {/* <div className="h-2 w-2 rounded-full bg-green-500" /> */}
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-sm text-foreground">Passed</span>
              </div>
              <span className="text-xl font-semibold text-green-500">
                {recordsData?.healthcheck?.passedChecks || 0}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
